import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Zone from '@wix/da-gruser-shared/pkg/components/Zone';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import LazyContext from '@wix/da-shared-react/pkg/LazyLoadingImage/LazyContext';
import { getProfileOwnerUser } from '../../../selectors/users';
import { getHomeWidgetConfigs } from './homeWidgetConfigs';

import s from './HomeSection.scss';

export interface Props {}

const HomeSection: React.FC<Props> = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profileOwnerUser = useSelector(getProfileOwnerUser);
  const isMobile = useContext(MobileContext);

  const homeWidgetConfigs = getHomeWidgetConfigs({ t, dispatch });

  const renderOneColumn = () => (
    <>
      <Zone
        name="topRight"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.topRight}
      />
      <Zone
        name="megaBar"
        direction="row"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.megaBar}
      />
      <Zone
        name="topBar"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.topBar}
      />
      {/*
      // Leaving this commented out to preserve the order of the zones on mobile
      // as requested by the product.
      // We originally launched Eclipse with Exhibionist module being
      // pre-installed in the topLeft zone, but we've later disabled option to
      // install it there. If we do not include this zone all the accounts with
      // pre-installed exhibitionist modules will lose them.
      // The reason why it is commented out is that the new gruser port does
      // not yet merge AddWidgets and will look ugly if we leave it in.
      <Zone
        name="topLeft"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.topLeft}
      />
      */}
      <Zone
        name="midRight"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.midRight}
      />
      <Zone
        name="bottomLeft"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.bottomLeft}
      />
      <Zone
        name="bottomRight"
        mapModuleTypeToWidgetConfig={homeWidgetConfigs.bottomRight}
      />
    </>
  );

  const renderTwoColumns = () => (
    <>
      <div className={s['zone-top']}>
        <Zone
          name="megaBar"
          direction="row"
          mapModuleTypeToWidgetConfig={homeWidgetConfigs.megaBar}
        />
        <Zone
          name="topBar"
          mapModuleTypeToWidgetConfig={homeWidgetConfigs.topBar}
        />
      </div>
      <div className={s['flex-wrap']}>
        <div className={s['zone-column-left']}>
          <Zone
            name="bottomLeft"
            mapModuleTypeToWidgetConfig={homeWidgetConfigs.bottomLeft}
          />
        </div>
        <div className={s['zone-column-right']}>
          <Zone
            name="topRight"
            mapModuleTypeToWidgetConfig={homeWidgetConfigs.topRight}
          />
          <Zone
            name="midRight"
            mapModuleTypeToWidgetConfig={homeWidgetConfigs.midRight}
          />
          <Zone
            name="bottomRight"
            mapModuleTypeToWidgetConfig={homeWidgetConfigs.bottomRight}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={s['root']}>
      <BiLoggerContextProvider
        value={{ creatorid: profileOwnerUser.useridUuid }}
      >
        <LazyContext.Provider value={true}>
          {isMobile ? renderOneColumn() : renderTwoColumns()}
        </LazyContext.Provider>
      </BiLoggerContextProvider>
    </div>
  );
};
HomeSection.displayName = 'HomeSection';

export default HomeSection;
